'use client';

import { useEffect, useRef } from 'react';
import { onOneTrustGroupsUpdated } from '../utils';
type ConsentGroup = 'C0001' | 'C0002' | 'C0003' | 'C0004';
interface UseConsentGatedInitParams {
  src: string;
  consentGroup: ConsentGroup;
  /**
   * Called immediately before `OneTrust.InsertScript`.
   * Use for bootstrap work that must happen before the script loads (e.g. GTM `gtm.start`).
   */
  onBeforeInsert?: () => void;
  /**
   * Called after OneTrust inserts the script.
   * If omitted, the hook simply inserts the script with no callback.
   */
  onReady?: () => void;
}

/**
 * Loads a 3rd-party script via `OneTrust.InsertScript` -- the SDK's built-in
 * consent-gated loader. OneTrust checks consent internally, creates the
 * `<script>` element, and fires `onReady` once inserted.
 *
 * No `<script>` tag in JSX is needed -- OneTrust creates the element itself.
 * `onReady` is stored in a ref so callers don't need to memoize it.
 */
export function useConsentGatedInit({
  src,
  consentGroup,
  onBeforeInsert,
  onReady
}: UseConsentGatedInitParams) {
  const onBeforeInsertRef = useRef(onBeforeInsert);
  onBeforeInsertRef.current = onBeforeInsert;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    let called = false;
    const init = () => {
      if (called) return;
      called = true;
      onReadyRef.current?.();
    };
    const insert = () => {
      if (called) return;
      onBeforeInsertRef.current?.();
      window.OneTrust?.InsertScript?.(src, 'body', init, null, consentGroup, true);
    };
    insert();
    return onOneTrustGroupsUpdated(insert);
  }, [src, consentGroup]);
}